/**
 * The account control (D157): the shell's stateful half of the account
 * dialog — who is signed in, and what the last door said.
 *
 * @remarks
 * The dialog in `player-ui` is pure; this component owns the two things it
 * cannot: the session read from the identity client on mount, and the
 * notice each door leaves. A door that changes who the play cookie means
 * — a passkey sign-in, a sign-out — hands control to `onIdentityChanged`,
 * because the open session belonged to the previous owner and the shell
 * must open again as the new one (the app reloads). A door that redirects
 * (Google, Apple, the magic link's own arrival) changes nothing here: the
 * browser leaves, and the next load reads the new session. D166 adds the
 * account's pass to the signed-in half, through `usePassPanel`. D170: on a
 * creator's own domain the signed-out half is one door — the trip to the
 * home site and back — and the session read is the bearer's. D176: the
 * frame is a choice — the control row's dialog, or the handoff page's
 * inline panel — and the wiring is the same either way.
 *
 * @packageDocumentation
 */

import type { DoorsAnswer, SignedInUser, SocialProvider } from '@orpheus/api-contracts';

import { AccountDialogComponent, type AccountNotice, AccountPanelComponent } from '@orpheus/player-ui';
import { useEffect, useRef, useState } from 'react';

import type { DoorOutcome, IdentityClient } from './identity-client.js';
import type { StoreClient } from './store-client.js';

import { ACCOUNT_NOTICE, HANDOFF_NOTICE, linkFailureNoticeOf } from './account-copy.js';
import { useDevicesPanel } from './use-devices-panel.js';
import { usePassPanel } from './use-pass-panel.js';

/** Props for {@link AccountControl}. */
export interface AccountControlProps {
	/** D170: a line the player arrives IN, like the link's — the handoff token that did not exchange. */
	readonly arrivalNotice?: string | undefined;
	/** The page's `location.search` on arrival — a magic link that did not open lands here with `?error=`. */
	readonly arrivalSearch: string;
	/**
	 * D170: on a creator's own domain, where the account's cookie cannot be
	 * seen — Sign in is a trip to this address and back. Absent, this is a
	 * home page and the doors open here.
	 */
	readonly handoffDepartureUrl?: string | undefined;
	readonly identity: IdentityClient;
	/** The play cookie now means someone else — the session must be opened again. */
	readonly onIdentityChanged: () => void;
	/** Drop the arrival's `?error=` from the address bar once it has been read, so a reload does not say it twice. */
	readonly onArrivalRead?: () => void;
	/** Where the browser goes for the pass (D166): Stripe's page. Defaults to the real navigation; a test hands in a recorder. */
	readonly navigate?: (url: string) => void;
	/** Now, for the devices panel's "last seen" (D171). Defaults to the clock; a test pins it. */
	readonly nowMs?: () => number;
	/**
	 * D176: the frame. `dialog` (the default) is the control row's — one
	 * button, a modal behind it. `inline` is the handoff page's (D170),
	 * where signing in is the whole page: the panel is drawn where it is
	 * put, with no trigger, no Close and no heading of its own.
	 */
	readonly presentation?: 'dialog' | 'inline';
	/** The store (D166), when the build has one: the account's pass is shown, and managed, from here. */
	readonly storefront?: StoreClient | undefined;
}


/** The real navigation: the browser leaves for Stripe's page. */
const goToPage = (url: string): void => {
	window.location.assign(url);
};

/** The real clock, for the devices panel's readings — the shell's edge, never the core's. */
const readClock = (): number => Date.now();

/**
 * Render the account control.
 *
 * @param props - {@link AccountControlProps}
 * @returns The account dialog, wired
 */
export const AccountControl = ({
	arrivalNotice: handoffArrivalNotice,
	arrivalSearch,
	handoffDepartureUrl,
	identity,
	navigate = goToPage,
	nowMs = readClock,
	onArrivalRead,
	onIdentityChanged,
	presentation = 'dialog',
	storefront,
}: AccountControlProps) => {
	const [account, setAccount] = useState<SignedInUser | undefined>(undefined);
	// D173: which doors to draw — asked once, with the session; unanswered, the dialog draws the two every deployment has.
	const [doors, setDoors] = useState<DoorsAnswer | undefined>(undefined);
	// A link that did not open is the one state the player arrives IN rather
	// than reaches: the dialog opens on it, saying why, so the way back in is
	// on screen rather than behind a button. D170: a handoff token that did
	// not exchange is the same state, arrived at by the other route.
	const linkNotice = useRef(linkFailureNoticeOf(arrivalSearch)).current;
	const arrivalNotice = linkNotice ?? handoffArrivalNotice;
	// Every line a player can ARRIVE with is a refusal: a link that did not open, a token that did not exchange.
	const [notice, setNotice] = useState<AccountNotice | undefined>(
		arrivalNotice === undefined ? undefined : { kind: 'refused', line: arrivalNotice },
	);

	useEffect(() => {
		if (linkNotice !== undefined) {
			onArrivalRead?.();
		}
		// Once, on arrival: the address bar is cleaned the moment the notice is taken from it.
		// eslint-disable-next-line react-hooks/exhaustive-deps -- arrival happens once; a re-render must not re-clean
	}, []);

	useEffect(() => {
		let isDisposed = false;

		identity
			.readSession()
			.then((user) => {
				if (!isDisposed) {
					setAccount(user);
				}
			})
			// An identity Worker that cannot be reached is a signed-out player, not a broken shell.
			.catch(() => undefined);
		identity
			.readDoors()
			.then((offered) => {
				if (!isDisposed) {
					setDoors(offered);
				}
			})
			// Unanswered is a state the dialog knows: the passkey and the link, no providers.
			.catch(() => undefined);

		return () => {
			isDisposed = true;
		};
	}, [identity]);

	/** Say what a door did, once it has. */
	const say = (line: string) => () => {
		setNotice({ kind: 'said', line });
	};

	// D166: the account's pass — a line and a button, asked of the store once the account is known.
	const pass = usePassPanel({ accountId: account?.id, navigate, say: setNotice, storefront });
	// D171: where the account is signed in, asked of the identity once the account is known.
	const devices = useDevicesPanel({ accountId: account?.id, identity, nowMs, say: setNotice });

	/** Go through one door: a refusal (or a throw) becomes the notice; success runs its consequence. */
	const through = (door: () => Promise<DoorOutcome>, onDone: () => void): void => {
		setNotice(undefined);
		door()
			.then((outcome) => {
				if (outcome.kind === 'refused') {
					setNotice({ kind: 'refused', line: outcome.reason });
				} else {
					onDone();
				}
			})
			// A throw here is the network, not a door: the Worker could not be
			// reached at all. The browser's own words for that ("Failed to fetch")
			// are true and not the register, so the shell says it (Mike, 2026-09-11).
			.catch(() => {
				setNotice({ kind: 'refused', line: ACCOUNT_NOTICE.refused.unreachable });
			});
	};

	const panel = {
		account: account === undefined ? undefined : { email: account.email },
		devices,
		doors,
		elsewhere:
			handoffDepartureUrl === undefined
				? undefined
				: {
					label: HANDOFF_NOTICE.awayLabel,
					lead: HANDOFF_NOTICE.awayLead,
					onGo: () => {
						navigate(handoffDepartureUrl);
					},
				},
		notice,
		onAddPasskey: () => {
			through(async () => identity.addPasskey(), say(ACCOUNT_NOTICE.passkeyAdded));
		},
		onSendMagicLink: (email: string) => {
			through(async () => identity.sendMagicLink(email), say(ACCOUNT_NOTICE.linkSent));
		},
		onSignInWithPasskey: () => {
			through(async () => identity.signInWithPasskey(), onIdentityChanged);
		},
		onSignInWithSocial: (provider: SocialProvider) => {
			through(async () => identity.signInWithSocial(provider), onIdentityChanged);
		},
		onSignOut: () => {
			through(async () => identity.signOut(), onIdentityChanged);
		},
		pass,
	};

	return presentation === 'inline' ? <AccountPanelComponent {...panel} /> : <AccountDialogComponent {...panel} isInitiallyOpen={arrivalNotice !== undefined} />;
};
